import { Injectable, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { NotificationsHub, SessionHub } from '@signalr/index';
import { UsageUpdatedMessage, SubscriptionPlanChangedMessage, LlmStreamChunkMessage, LlmStreamCompletedMessage, LlmStreamFailedMessage, AgentResult, GetToolResult, CreateResult, TestDto, TestRunSummary, AgentSessionDto, BenchmarkRunUpdatedMessage, BenchmarkRunItemUpdatedMessage } from '@generated/models';
import { AuthService } from './auth.service';
import { ConversationDto } from '@generated/models/conversation-dto';

@Injectable({
  providedIn: 'root'
})
export class Signalr {
  notificationsHub = inject(NotificationsHub);
  sessionHub = inject(SessionHub);
  private authService = inject(AuthService);

  isConnected = signal(false);
  usageData = signal<UsageUpdatedMessage | null>(null);
  subscriptionPlanChanged = signal<SubscriptionPlanChangedMessage | null>(null);

  llmStreamChunk$ = new Subject<LlmStreamChunkMessage>();
  llmStreamCompleted$ = new Subject<LlmStreamCompletedMessage>();
  llmStreamFailed$ = new Subject<LlmStreamFailedMessage>();
  agentUpdated$ = new Subject<AgentResult>();
  toolUpdated$ = new Subject<GetToolResult>();
  agentCreated$ = new Subject<CreateResult>();
  toolCreated$ = new Subject<CreateResult>();
  testCreated$ = new Subject<CreateResult>();
  testUpdated$ = new Subject<TestDto>();
  testRunUpdated$ = new Subject<TestRunSummary>();
  /** AgentSessionUpdated — единственный сигнал по сессии (SessionFinalized
   *  схлопнули в него). Полный AgentSessionDto с history + audio_url +
   *  synthesized_*, компоненты детали сессии могут применять напрямую
   *  без дополнительного GET'а. */
  agentSessionUpdated$ = new Subject<AgentSessionDto>();
  /** ConversationUpdated — разбор разговора сдвинулся. Payload идентичен
   *  ответу GET /api/Analytics/conversations/{id}: карточка применяет
   *  напрямую, список берёт из него свои несколько полей. */
  conversationUpdated$ = new Subject<ConversationDto>();
  /** BenchmarkRunUpdated — статус parent-рана (agrregate). Только
   *  status + counters, без метрик. Обновлять UI списка parent-runs. */
  benchmarkRunUpdated$ = new Subject<BenchmarkRunUpdatedMessage>();
  /** BenchmarkRunItemUpdated — per-версия. Все метрики (LLM/STT) здесь.
   *  Обновлять row в expanded-run details, latest_benchmark на модели
   *  и live-панель в «Run benchmark» drawer'e. */
  benchmarkRunItemUpdated$ = new Subject<BenchmarkRunItemUpdatedMessage>();

  async start(token?: string): Promise<void> {
    // Subscribe to events
    this.notificationsHub.onUsageUpdated((message) => {
      console.log('UsageUpdated received:', message);
      this.usageData.set(message);
    });

    this.notificationsHub.onSubscriptionPlanChanged((message) => {
      console.log('SubscriptionPlanChanged received:', message);
      this.subscriptionPlanChanged.set(message);
    });

    this.notificationsHub.onLlmStreamChunk((message) => this.llmStreamChunk$.next(message));
    this.notificationsHub.onLlmStreamCompleted((message) => this.llmStreamCompleted$.next(message));
    this.notificationsHub.onLlmStreamFailed((message) => this.llmStreamFailed$.next(message));
    this.notificationsHub.onAgentUpdated((message) => this.agentUpdated$.next(message));
    this.notificationsHub.onToolUpdated((message) => this.toolUpdated$.next(message));
    this.notificationsHub.onAgentCreated((message) => this.agentCreated$.next(message));
    this.notificationsHub.onToolCreated((message) => this.toolCreated$.next(message));
    this.notificationsHub.onTestCreated((message) => this.testCreated$.next(message));
    this.notificationsHub.onTestUpdated((message) => this.testUpdated$.next(message));
    this.notificationsHub.onTestRunUpdated((message) => this.testRunUpdated$.next(message));
    this.notificationsHub.onAgentSessionUpdated((message) => this.agentSessionUpdated$.next(message));
    this.notificationsHub.onConversationUpdated((message) => this.conversationUpdated$.next(message));
    this.notificationsHub.onBenchmarkRunUpdated((message) => this.benchmarkRunUpdated$.next(message));
    this.notificationsHub.onBenchmarkRunItemUpdated((message) => this.benchmarkRunItemUpdated$.next(message));

    try {
      const authToken = token || this.authService.getAccessToken() || '';
      await this.notificationsHub.connect(authToken);
      this.isConnected.set(true);
      console.log('SignalR connected');
    } catch (error) {
      console.error('SignalR connection error:', error);
      this.isConnected.set(false);
    }
  }

  private sessionConnectPromise: Promise<void> | null = null;

  connectSession(sessionToken: string): Promise<void> {
    this.sessionConnectPromise = (async () => {
      try {
        await this.sessionHub.connect(sessionToken);
        console.log('Session hub connected');
      } catch (error) {
        console.error('Session hub connection error:', error);
      }
    })();
    return this.sessionConnectPromise;
  }

  async disconnectSession(): Promise<void> {
    // Hard abort — don't wait for any in-flight connect. The hub itself
    // nulls its connection synchronously and lets stop() run unawaited
    // (the WS close frame goes out on the first tick). Awaiting either
    // side here is what used to hang the Start button on reconnect.
    this.sessionConnectPromise = null;
    try {
      await this.sessionHub.disconnect();
    } catch (error) {
      console.warn('Session hub disconnect error (ignored):', error);
    }
  }

  async stop(): Promise<void> {
    await this.notificationsHub.disconnect();
    this.isConnected.set(false);
  }
}
